// Mobile UI route table — the Flutter navigation graph.
//
// Flutter pushes MaterialPageRoutes; here each of those destinations is a route
// so the Android hardware back button and deep links behave the same way.
//
//   /splash                  splashScreen.dart      (boot gate)
//   /login /signup /forgot   the three auth screens
//   /blocked                 blockedScreen.dart
//   /home?tab=N              homeScreen.dart        (4 bottom tabs)
//   /rooms/:locationId       roomsScreen.dart
//   /room/:roomId/devices    devicesScreen.dart
//   /device/:uniqueNumber    screenRedirector()     -> per-type control screen
//
// Screens that are identical on both form factors (Wall Mode, per-room Scenes,
// Profile, About, Schedules) are reused from ui/desktop rather than duplicated —
// they read the same core logic, which is the whole point of the split.

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useApp } from '../../core/state/AppContext';
import { kioskLandingRoute } from '../../core/wall/kioskMode';
import { MessageDialogProvider } from './components/MessageDialog';

import SplashScreen from './screens/SplashScreen';
import BlockedScreen from './screens/BlockedScreen';
import LoginScreen from './screens/LoginScreen';
import SignUpScreen from './screens/SignUpScreen';
import ForgotPasswordScreen from './screens/ForgotPasswordScreen';
import HomeScreen from './screens/HomeScreen';
import RoomsScreen from './screens/RoomsScreen';
import DevicesScreen from './screens/DevicesScreen';
import DeviceScreen from './screens/device/DeviceScreen';
import LocationDataScreen from './screens/LocationDataScreen';
import WifiOnboardScreen from './screens/WifiOnboardScreen';
import AddDeviceScreen from './screens/AddDeviceScreen';
import BleTestScreen from './screens/BleTestScreen';
import MqttConsoleScreen from './screens/MqttConsoleScreen';
import TuyaTestingScreen from './screens/TuyaTestingScreen';
import FirmwareScreen from './screens/FirmwareScreen';

// Shared with the desktop shell — same component, same core logic.
import RoomScenesScreen from '../desktop/pages/scenes/RoomScenesScreen';
import ProfileScreen from '../desktop/pages/ProfileScreen';
import AboutScreen from '../desktop/pages/AboutScreen';
import SchedulesScreen from '../desktop/pages/SchedulesScreen';
import MobileShell from './components/MobileShell';
import WallModeScreen from '../desktop/pages/wall/WallModeScreen';
import WallDesignerScreen from '../desktop/pages/wall/WallDesignerScreen';
import WallPanelSetupScreen from '../shared/WallPanelSetupScreen';

function Protected({ children, bare = false }) {
  const { isAuthed, signingIn } = useApp();
  // Same rule as desktop: a saved sign-in in flight is "not yet decided", not
  // signed-out. Without this the login screen flashes before the session lands.
  if (signingIn) return <SplashScreen />;
  if (!isAuthed) return <Navigate to="/login" replace />;
  // ── The identity strip and company footer live HERE ──────────────────────
  // Attaching them to `Protected` rather than to each screen means every
  // authenticated route has them by construction, and a screen added tomorrow
  // cannot forget. The pre-auth routes below are deliberately not wrapped:
  // there is no user yet to name.
  //
  // `bare` opts a route out — Wall Mode is a kiosk that deliberately fills the
  // panel edge to edge, and chrome on it would be wrong rather than missing.
  return bare ? children : <MobileShell>{children}</MobileShell>;
}

export default function MobileRoutes() {
  const { isAuthed } = useApp();

  return (
    <MessageDialogProvider>
      <Routes>
        <Route path="/splash" element={<SplashScreen />} />
        <Route path="/blocked" element={<BlockedScreen />} />
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/signup" element={<SignUpScreen />} />
        <Route path="/forgot" element={<ForgotPasswordScreen />} />

        <Route path="/home" element={<Protected><HomeScreen /></Protected>} />
        <Route path="/rooms/:locationId" element={<Protected><RoomsScreen /></Protected>} />
        <Route path="/room/:roomId/devices" element={<Protected><DevicesScreen /></Protected>} />
        <Route path="/device/:uniqueNumber" element={<Protected><DeviceScreen /></Protected>} />
        <Route path="/location/:locationId" element={<Protected><LocationDataScreen /></Protected>} />
        {/* BLE Wi-Fi onboarding — hand a brand-new device its home Wi-Fi. */}
        <Route path="/wifi-setup" element={<Protected><WifiOnboardScreen /></Protected>} />
        {/* Claim an existing device: by its own number, or by a share code.
            Both render AddDeviceScreen, which adapts its wording. */}
        <Route path="/add-device" element={<Protected><AddDeviceScreen /></Protected>} />
        <Route path="/scan-share" element={<Protected><AddDeviceScreen /></Protected>} />
        {/* Firmware bring-up tool — Super Admin only, gated in MeTab. */}
        <Route path="/ble-test" element={<Protected><BleTestScreen /></Protected>} />
        {/* Smartify Message Box — Super Admin, gated in MeTab. */}
        <Route path="/mqtt" element={<Protected><MqttConsoleScreen /></Protected>} />
        {/* Production bench — AllowTestingTuya (or Super Admin); the tile in
            MeTab and the screen itself both read AppContext's canTuyaTest. */}
        <Route path="/tuya-testing" element={<Protected><TuyaTestingScreen /></Protected>} />
        {/* Estate-wide firmware list — Super Admin, gated in MeTab and inside. */}
        <Route path="/firmware" element={<Protected><FirmwareScreen /></Protected>} />

        <Route path="/room/:roomId/scenes" element={<Protected><RoomScenesScreen /></Protected>} />
        <Route path="/schedule/:uniqueNumber/:output" element={<Protected><SchedulesScreen /></Protected>} />
        <Route path="/profile" element={<Protected><ProfileScreen /></Protected>} />
        <Route path="/about" element={<Protected><AboutScreen /></Protected>} />
        {/* Kiosk: fills the panel, no app chrome. */}
        <Route path="/wall" element={<Protected bare><WallModeScreen /></Protected>} />
        <Route path="/wall/design" element={<Protected bare><WallDesignerScreen /></Protected>} />
        <Route path="/wall/setup" element={<Protected><WallPanelSetupScreen /></Protected>} />

        {/* A wall tablet lands on its panel instead of the normal home. Only the
            LANDING route is redirected — forcing /wall on every navigation would
            make the designer unreachable from the very tablet it configures. */}
        <Route
          path="*"
          element={
            // eslint-disable-next-line no-nested-ternary
            !isAuthed ? <Navigate to="/login" replace />
              : <Navigate to={kioskLandingRoute() || '/splash'} replace />
          }
        />
      </Routes>
    </MessageDialogProvider>
  );
}
